import React, { useCallback, useMemo } from "react";
import { Typography } from "@mui/material";
import Select from "react-select";
import { ControlWrapper } from "./ControlWrapper";

/**
 * Selector de hasta dos valores con prioridad.
 *
 * `value` es el objeto completo y `onChange` recibe un objeto con ambos IDs.
 *
 * Las opciones utilizan el formato { value, label } de react-select.
 */
export const DualSelect = ({
  principal,
  secundario,
  options = [],
  value,
  onChange,
  label,
  placeholder = "Seleccione...",
  errors = [],
  start,
  end,
  Wrapper = ControlWrapper,
  isClearable = true,
  type = "number",
  ...selectProps
}) => {
  const toKey = useCallback((id) =>
    id === null || id === undefined || id === "" ? null : String(id), []);

  const parseId = useCallback((id) => {
    if (id === null || id === undefined || id === "") return null;
    return type === "number" ? Number(id) : id;
  }, [type]);

  const selected = useMemo(() => {
    const principalId = toKey(value?.[principal]);
    const secundarioId = toKey(value?.[secundario]);
    const find = (key) => options.find((option) => toKey(option.value) === key);
    const result = [];
    if (principalId !== null) {
      const option = find(principalId);
      if (option) result.push(option);
    }
    if (secundarioId !== null && secundarioId !== principalId) {
      const option = find(secundarioId);
      if (option) result.push(option);
    }
    return result;
  }, [value, principal, secundario, options, toKey]);

  const handleChange = useCallback((nextOptions) => {
    const next = [];
    for (const option of nextOptions || []) {
      if (option && !next.some((saved) => toKey(saved.value) === toKey(option.value))) {
        next.push(option);
      }
      if (next.length === 2) break;
    }

    // La primera seleccionada sigue siendo la principal.
    // Al quitarla, react-select devuelve la secundaria como primera.
    const primaryValue = parseId(next[0]?.value);
    const secondaryValue = parseId(next[1]?.value);
    onChange({
      [principal]: primaryValue,
      [secundario]: secondaryValue,
    });
  }, [onChange, principal, secundario, parseId, toKey]);

  const isOptionDisabled = useCallback(
    (option) => selected.length >= 2 && !selected.some(
      (selectedOption) => toKey(selectedOption.value) === toKey(option.value)
    ),
    [selected, toKey]
  );

  return (
    <Wrapper name={principal} label={label} errors={errors} start={start} end={end}>
      <Select
        {...selectProps}
        inputId={principal}
        isMulti
        options={options}
        value={selected}
        onChange={handleChange}
        isOptionDisabled={isOptionDisabled}
        isClearable={isClearable}
        closeMenuOnSelect={false}
        placeholder={placeholder}
        className="flex-grow-1 h-100 w-100"
        noOptionsMessage={() => "Sin opciones"}
        formatOptionLabel={(option, { context }) => (
          <Typography color={context === "menu" ? "black" : "inherit"}>{option.label}</Typography>
        )}
        styles={{
          ...selectProps.styles,
          menu: (base, state) => ({
            ...base,
            ...(selectProps.styles?.menu?.(base, state) || {}),
            zIndex: 50000,
          }),
          multiValue: (base, state) => ({
            ...base,
            ...(selectProps.styles?.multiValue?.(base, state) || {}),
            backgroundColor: toKey(state.data.value) === toKey(selected[0]?.value)
              ? "#dbeafe"
              : "#eef0f3",
          }),
          multiValueLabel: (base, state) => ({
            ...base,
            ...(selectProps.styles?.multiValueLabel?.(base, state) || {}),
            color: toKey(state.data.value) === toKey(selected[0]?.value)
              ? "#1e40af"
              : "#374151",
            fontWeight: toKey(state.data.value) === toKey(selected[0]?.value) ? 700 : 400,
          }),
        }}
      />
    </Wrapper>
  );
};
